import { useContext } from "react";
import { TranslationService } from "../../../services/TranslationService";
import ModalService from "../../shared/bootstrap/modal/Modal";
import Dropdown from "../../shared/components/Dropdown";
import TooltipComponent from "../../shared/TooltipComponent";
import CriteriaContext, {
  CriterionModel,
  CriterionType
} from "../providers/CriteriaContext";
import ButtonLink from "./form/ButtonLink";
import { RequiredManager } from "../../shared/RequieredManager";
import { useCriterionLogic } from "./criterion-parts/useCriterionLogic";
import { NumberAmountCriterion } from "./criterion-parts/NumberAmountCriterion";
import { DateCriterion } from "./criterion-parts/DateCriterion";
import { ActivityTypeCriterion } from "./criterion-parts/ActivityTypeCriterion";
import { ClientInvoiceCriterion } from "./criterion-parts/ClientInvoiceCriterion";

export type CriterionDefinition = {
  id: number;
  name: string;
  type: CriterionType;
  tooltip: string | null;
};
export type AdditionalDefinitionItems = {
  AdditionalDefinitionItemID: number;
  Value: string;
}[];
export type AdditionalDefinition = {
  AdditionalDefinitionID: number;
  Name: string;
  Field: number;
  type: number;
  AdditionalDefinitionItems: AdditionalDefinitionItems;
};
export type ExtraDefinitions = {
  criterionList: CriterionDefinition[];
  clientDefs: AdditionalDefinition[];
  invoiceDefs: AdditionalDefinition[];
};

type DragEvents = {
  onDragEnter: (event: React.DragEvent<HTMLElement>) => void;
  onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd: (e: React.DragEvent<HTMLElement>) => void;
  draggable: boolean;
};

const Criterion = ({
  first,
  data,
  extraDefinitions,
  requiredManager,
  dragEvents,
  index
}: {
  first: boolean;
  data: CriterionModel;
  extraDefinitions: ExtraDefinitions;
  requiredManager: RequiredManager;
  dragEvents?: DragEvents;
  index?: number;
}) => {
  const { translate } = TranslationService;
  const { deleteCriterion } = useContext(CriteriaContext);
  const { criterionList } = extraDefinitions;

  const {
    criterion,
    criterionIsNumber,
    activityTypes,
    inputResetKey,
    handleCriterion,
    handleDefinition,
    handleOperator,
    handleValue,
    handleAndOr,
    handleList,
    handleActivityOperator,
    handleActivity,
    dateOperators,
    textOperators,
    amountOperators,
    listOperators,
    ioListOperators,
    dateOperatorsWithEmpty,
    currencySymbol,
    definitions,
    definition,
    activities,
    getListValue,
    dateOperatorsByDefault
  } = useCriterionLogic(data, extraDefinitions);

  const andOr = [
    { value: false, text: translate.And },
    { value: true, text: translate.Or }
  ];

  const onDeleteCriterion = (key: string) => {
    ModalService.showDefaultModal({
      onAcceptClick: () => deleteCriterion(key),
      acceptButtonLabel: TranslationService.translate.Delete,
      acceptButtonClassName: "btn btn-danger",
      message: TranslationService.translate.SureDeleteCriteria,
      title: TranslationService.translate.Delete
    });
  };

  const listValue = getListValue(definition);

  const renderCriterionByType = () => {
    if (!criterion) return null;

    switch (criterion.type) {
      case CriterionType.Number_Amount:
        return (
          <NumberAmountCriterion
            data={data}
            criterionIsNumber={criterionIsNumber}
            criterionId={criterion.id}
            amountOperators={amountOperators}
            currencySymbol={currencySymbol}
            inputResetKey={inputResetKey}
            onOperatorChange={handleOperator}
            onValueChange={handleValue}
            requiredManager={requiredManager}
          />
        );

      case CriterionType.Date:
        return (
          <DateCriterion
            data={data}
            dateOperators={dateOperatorsByDefault}
            inputResetKey={inputResetKey}
            onOperatorChange={handleOperator}
            onValueChange={handleValue}
            requiredManager={requiredManager}
          />
        );

      case CriterionType.Client:
      case CriterionType.Invoice:
        return (
          <ClientInvoiceCriterion
            data={data}
            definitions={definitions}
            definition={definition}
            textOperators={textOperators}
            listOperators={listOperators}
            ioListOperators={ioListOperators}
            dateOperators={dateOperators}
            dateOperatorsWithEmpty={dateOperatorsWithEmpty}
            inputResetKey={inputResetKey}
            listValue={listValue}
            onDefinitionChange={handleDefinition}
            onOperatorChange={handleOperator}
            onValueChange={handleValue}
            onListChange={handleList}
            requiredManager={requiredManager}
            translate={translate}
          />
        );

      case CriterionType.ActivityType:
        return (
          <ActivityTypeCriterion
            data={data}
            dateOperators={dateOperatorsByDefault}
            listOperators={listOperators}
            activities={activities}
            activityTypes={activityTypes}
            inputResetKey={inputResetKey}
            onOperatorChange={handleOperator}
            onValueChange={handleValue}
            onActivityOperatorChange={handleActivityOperator}
            onActivityChange={handleActivity}
            requiredManager={requiredManager}
          />
        );

      default:
        return null;
    }
  };

  const dragEventHandlers =
    dragEvents && index !== undefined
      ? {
          onDragEnter: dragEvents.onDragEnter,
          onDragStart: dragEvents.onDragStart,
          onDragEnd: dragEvents.onDragEnd,
          draggable: dragEvents.draggable
        }
      : {};

  return (
    <div
      className={
        "col-12 mb-0 pt-3 criterion " + (data.highlight ? "rowHighlight" : "")
      }
      {...dragEventHandlers}
      style={{ cursor: dragEvents?.draggable ? "move" : "default" }}
    >
      <div className="d-flex align-items-center">
        {dragEvents?.draggable && (
          <div className="me-2" style={{ cursor: "grab", userSelect: "none" }}>
            <i className="fa-solid fa-grip-vertical text-muted" />
          </div>
        )}
        <div className="form-row d-flex gap-2">
          {!first && (
            <Dropdown
              onChange={handleAndOr}
              defaultValue={data.ConditionOR}
              items={andOr}
              selectStyle="selectAndOrWidth"
            />
          )}

          <Dropdown
            onChange={handleCriterion}
            optionLabel={translate.Select}
            defaultValue={criterionList.find((x) => x.id === data.Field)}
            items={criterionList.map((x) => ({ text: x.name, value: x }))}
          />

          {renderCriterionByType()}
        </div>

        <div className="ms-auto">
          <TooltipComponent title={TranslationService.translate.Delete}>
            <ButtonLink
              className="a-button btn btn-link"
              onClick={() => onDeleteCriterion(data.key)}
            >
              <i className="fa-solid fa-trash text-danger" />
            </ButtonLink>
          </TooltipComponent>
        </div>
      </div>
      <hr style={{ color: "grey", marginBottom: 0 }} />
    </div>
  );
};

export default Criterion;
